import { Service, ServicePromise } from './services';
import config from '../config/config';

export const fontFamilies = {
  "serif": [
    'Georgia', 
    'Times New Roman', 
    'Times', 
    'Merryweather', 
    'Palatino', 
    'Garamond', 
    'Book Antiqua',
    'Baskerville', 
    'Charter', 
    'Georgia Pro', 
    'Serifa', 
    'Caecilia', 
    'Tisa',
    'Minion Pro',
    'Bookman', 
    'New York', 
    'Baskerville Old Face', 
    'Century Schoolbook', 
    'Hoefler Text',
    'Cambria', 
    'Goudy Old Style', 
    'Didot',
    'Times PS'
  ],
  "sans-serif": [
    'Arial', 
    'Helvetica', 
    'Verdana', 
    'Tahoma', 
    'Trebuchet MS', 
    'Geneva', 
    'Lucida Grande', 
    'Lucida Sans Unicode', 
    'Segoe UI', 
    'Calibri', 
    'Arial Narrow',
    'Roboto', 
    'Open Sans', 
    'Lato', 
    'Montserrat', 
    'Helvetica Neue',
    'Source Sans Pro', 
    'Nimbus Sans', 
    'Lucida Sans', 
    'Corbel', 
    'Fira Sans',
    'MS Sans Serif',
    'Arial Rounded MT Bold',
    'MS Gothic',
    'Geneva',
    'Lucida Sans Regular'
  ],
  "monospace": [
    'Courier New', 
    'Courier', 
    'Lucida Console', 
    'Consolas', 
    'Monaco', 
    'Andale Mono', 
    'Inconsolata', 
    'Source Code Pro', 
    'Fira Code', 
    'Roboto Mono', 
    'PT Mono',
    'Cousine',
    'Liberation Mono', 
    'Oxygen Mono', 
    'DejaVu Sans Mono', 
    'Menlo', 
    'Bitstream Vera Sans Mono', 
    'Droid Sans Mono',
    'Tecton',
    'Fixedsys'
  ],
  "cursive": [
    'Comic Sans MS', 
    'Brush Script MT', 
    'Lucida Handwriting', 
    'Pacifico', 
    'Dancing Script', 
    'Great Vibes', 
    'Sacramento', 
    'Satisfy', 
    'Allura', 
    'Zeyada',
    'Curlz MT', 
    'Droid Serif',
    'Lucida Calligraphy',
    'Mistral',
    'Zapfino'
  ],
  "fantasy": [
    'Impact', 
    'Charcoal', 
    'Arial Black', 
    'Comic Sans MS', 
    'Papyrus', 
    'Georgia', 
    'Forte', 
    'Rockwell', 
    'Verdana', 
    'Courier New', 
    'Lucida Calligraphy',
    'Algerian', 
    'Jokerman', 
    'Parchment', 
    'Stencil',
    'Curlz MT',
    'Trebuchet MS'
  ],
  // "system-ui": [
  //   'system-ui', 
  //   '-apple-system', 
  //   'BlinkMacSystemFont', 
  //   '"Segoe UI"', 
  //   '"Roboto"', 
  //   '"Oxygen"', 
  //   '"Ubuntu"', 
  //   '"Cantarell"', 
  //   '"Fira Sans"', 
  //   '"Droid Sans"', 
  //   '"Helvetica Neue"', 
  //   '"Arial"'
  // ]
};

type FontFam = | { src: string; family?: never } | { src?: never; family: string };

function getGenericFontType(fontFamily: string): string | undefined {
  for (const genericType in fontFamilies) {
    if (fontFamilies[genericType as keyof typeof fontFamilies].includes(fontFamily)) {
      return genericType;
    }
  }
  return undefined;
}

const FontService = ((): typeof service extends Service ? typeof service : never => {
  const service = {
    loadFont: (font: FontFam, className: string): ServicePromise => {

      // last minute hack to keep dev and prod env working seamlessly when
      // loading font files
      if (font.src) {
        if (config.ENVIRONMENT === "PRODUCTION") {
          if (font.src.startsWith(config.ASSETS.DEVELOPMENT)) {
            font.src = config.ASSETS.PRODUCTION + font.src.slice(config.ASSETS.DEVELOPMENT.length);
          }
          if (font.src.startsWith("http://" + config.ASSETS.DEVELOPMENT)) {
            font.src = config.ASSETS.PRODUCTION + font.src.slice(("http://" + config.ASSETS.DEVELOPMENT).length);
          }
          if (font.src.startsWith("https://" + config.ASSETS.DEVELOPMENT)) {
            font.src = config.ASSETS.PRODUCTION + font.src.slice(("https://" + config.ASSETS.DEVELOPMENT).length);
          }
        }

        if (config.ENVIRONMENT === "DEVELOPMENT") {
          if (!font.src.startsWith(config.ASSETS.DEVELOPMENT)) {
            font.src = config.ASSETS.DEVELOPMENT + font.src.slice(13);
          }
        }
      }


  // console.log('altered src: ', src );

      const existingStyleElement = document.getElementById(`style-${className}`);
      if (existingStyleElement) {
        existingStyleElement.remove();
      }
    
      const styleElement = document.createElement('style');
      styleElement.id = `style-${className}`;
      styleElement.type = `text/css`;
    
      let fontFace = '';
      if (font.src) {
        fontFace = `
          @font-face {
            font-family: '${className}-family';
            src: url('${font.src}');
          }
        `
      }
    
      const css = `
        ${fontFace}
        .${className} {
          font-family: ${font.family ? `'${font.family}', '${getGenericFontType(font.family)}'` : `'${className}-family'`}
        }
      `;
    
      styleElement.appendChild(document.createTextNode(css));
      document.head.appendChild(styleElement);

      return new Promise(resolve => resolve({
        success: true,
        messages: [`CLIENT->SERVICES->FONTSERVICE->LOADFONT: Font loaded successfully.`]
      }));
    },
  }
  
  return service;
})();

export default FontService;
